import { useMemo } from "react";
import { FlexCenterWrapper } from "../components/DefaultNoDataWrapper";
import Flex from "../components/Flex";
import Text from "../components/Text";
import CustomTitle from "../components/app/CustomTitle";
import { INITIAL_F2F_FORM_OPTIONS } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/note-forms/initial-f2f/initial-f2f-form-options-constant";
import { ENCOUNTER_FORM_MAPPER } from "../pages/app/provider/scheduling/appointment/encounter/content-section/VisitNoteForms";
import {
  NonNullableDate,
  formatDateTime,
  formatTime,
} from "../utils/date-time-utility";
import EncounterAddendumSection, {
  EncounterAddendumPrint,
} from "./EncounterAddendumSection";
import { ENCOUNTER_PDF_PRINT_STYLES } from "./encounter-pdf-styles";
import { useResolveEncounterLogo } from "./use-resolve-encounter-logo";
import BirpNoteTemplate from "./birpNoteTemplate/BirpNoteTemplate";
import CollaborativeCareModelTemplate from "./collaborativeCareModelTemplate/CollaborativeCareModelTemplate";
import DapNoteTemplate from "./dapNoteTemplate/DapNoteTemplate";
import {
  KeyValPair,
  RenderTextWithContainerPattern,
  ReportContainer,
} from "./constant";
import IndividualTherapyNoteTemplate from "./individualTherapyNoteTemplate/IndividualTherapyNoteTemplate";
import InitialPsychEvolutionTemplate from "./initialPsychEvolutionTemplate/InitialPsychEvolutionTemplate";
import PremierMdF2fTemplate from "./premierMdF2fTemplate/PremierMdF2fTemplate";
import PreoperativeNoteTemplate from "./preoperativeNoteTemplate/PreoperativeNoteTemplate";
import PrimaryCareSoapNoteTemplate from "./primaryCareSoapNoteTemplate/PrimaryCareSoapNoteTemplate";
import ProgressNoteTemplate from "./progressNoteTemplate/ProgressNoteTemplate";
import PsychiatricNoteTemplate from "./psychiatricNoteTemplate/PsychiatricNoteTemplate";
import PsychiatricProgressTemplate from "./psychiatricProgressTemplate/PsychiatricProgressTemplate";
import PsychodynamicTherapyNoteTemplate from "./psychodynamicTherapyNoteTemplate/PsychodynamicTherapyNoteTemplate";
import PsychoeducationTherapyTemplate from "./psychoeducationTherapyTemplate/PsychoeducationTherapyTemplate";
import PsychotherapyNoteTemplate from "./psychotherapyNoteTemplate/PsychotherapyNoteTemplate";
import SoapNoteTemplate from "./soapNoteTemplate/SoapNoteTemplate";
import TreatmentPlanTemplate from "./treatmentPlanTemplate/TreatmentPlanTemplate";

export { useResolveEncounterLogo };

interface EncounterSummeryTemplateProps {
  data: any;
  providerSignature?: string;
  /** The signed addendum, printed below the original signature when the visit has one. */
  addendum?: EncounterAddendumPrint;
}

/**
 * Drops ROS/PE template entries whose description is blank/null/empty so that
 * only fields containing data (and their section headers) appear in the
 * Encounter Summary Report. See ECRH-5142.
 */
const filterEmptyTemplates = (templates: any) =>
  Array.isArray(templates)
    ? templates.filter((it) => !!it?.description?.trim())
    : templates;

const getTemplateByFormType = (data: any) => {
  if (INITIAL_F2F_FORM_OPTIONS.some(({ value }) => value === data?.formType)) {
    return <PremierMdF2fTemplate data={data} />;
  }
  switch (data?.formType) {
    case "TREATMENT_PLAN":
      return <TreatmentPlanTemplate data={data} />;
    case "PREOPERATIVE_NOTE":
      return <PreoperativeNoteTemplate data={data} />;
    case "PSYCHIATRIC_PROGRESS":
      return <PsychiatricProgressTemplate data={data} />;
    case "INITIAL_PSYCHOTHERAPY":
      return <InitialPsychEvolutionTemplate data={data} />;
    case "PSYCHIATRIC_NOTE":
      return <PsychiatricNoteTemplate data={data} />;
    case "PSYCHOTHERAPY_NOTE":
      return <PsychotherapyNoteTemplate data={data} />;
    case "PSYCHODYNAMIC_THERAPY_NOTE":
      return <PsychodynamicTherapyNoteTemplate data={data} />;
    case "PSYCHOEDUCATION_THERAPY":
      return <PsychoeducationTherapyTemplate data={data} />;
    case "INDIVIDUAL_THERAPY_NOTE":
      return <IndividualTherapyNoteTemplate data={data} />;
    case "COLLABORATIVE_CARE_MODEL":
      return <CollaborativeCareModelTemplate data={data} />;
    case "PRIMARY_CARE_SOAP_NOTE":
      return <PrimaryCareSoapNoteTemplate data={data} />;
    case "DAP_NOTE":
      return <DapNoteTemplate data={data} />;
    case "BIRP_NOTE":
      return <BirpNoteTemplate data={data} />;
    case "PROGRESS_NOTE":
      return <ProgressNoteTemplate data={data} />;
    case "SIMPLE_SOAP_NOTE":
    default:
      return <SoapNoteTemplate data={data} />;
  }
};

/**
 * EncounterSummeryTemplate — Router component that wraps common header/footer
 * around form-type-specific templates.
 *
 * @author [Rajkumar Rathod, Atish Bagate]
 */
const EncounterSummeryTemplate = ({
  data,
  providerSignature,
  addendum,
}: EncounterSummeryTemplateProps) => {
  // Strip empty ROS/PE fields before rendering the form-type template so blank
  // fields and their section headers are not populated in the report.
  const reportData = useMemo(() => {
    if (!data) return data;
    return {
      ...data,
      rosTemplates: filterEmptyTemplates(data?.rosTemplates),
      physicalTemplates: filterEmptyTemplates(data?.physicalTemplates),
    };
  }, [data]);

  return (
    <div
      className="encounter-pdf-root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <style>{ENCOUNTER_PDF_PRINT_STYLES}</style>
      {/* ── Common Header ── */}
      <Flex
        width={"100%"}
        height={"100%"}
        alignItems={"center"}
        justifyContent={"center"}
        flexDirection={"column"}
        mb={2}
      >
        {data?.providerGroupLogo && (
          <img
            src={data?.providerGroupLogo}
            alt="provider group logo"
            style={{
              maxWidth: "120px",
              maxHeight: "60px",
              objectFit: "contain",
            }}
          />
        )}
        <FlexCenterWrapper>
          <CustomTitle>{data?.providerGroupName ?? "eCareHealth"}</CustomTitle>
        </FlexCenterWrapper>
      </Flex>

      {/* ── Visit Details ── */}
      <ReportContainer label="Visit Details">
        <div style={{ padding: "2px" }}>
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <KeyValPair
                k="Visit Id"
                v={data?.appointmentId?.split("-")?.[0]?.toUpperCase() ?? "-"}
              />
              <KeyValPair k="Patient Name" v={data?.patientName ?? "-"} />
              <KeyValPair
                k="Dob"
                v={NonNullableDate(data?.patientDob) ?? "-"}
              />
            </div>
            <div style={{ flex: 1 }}>
              <KeyValPair
                k="Visit Date"
                v={NonNullableDate(data?.encounterDate) ?? "-"}
              />
              <KeyValPair
                k="Appointment Date & Time"
                v={formatDateTime(data?.appointmentDate) ?? "-"}
              />
              <KeyValPair k="Provider Name" v={data?.providerName ?? "-"} />
            </div>
            <div style={{ flex: 1 }}>
              <KeyValPair
                k="Visit Note"
                v={
                  data?.formType ? ENCOUNTER_FORM_MAPPER?.[data?.formType] : "-"
                }
              />
              <KeyValPair
                k="Relationship To Insured"
                v={data?.insuredRelationshipWithPatientLabel ?? "-"}
              />
              <KeyValPair k="Insurance Name" v={data?.insuranceName ?? "-"} />
            </div>
          </div>
        </div>
      </ReportContainer>

      {/* ── Chief Complaint (common) — shown right after Visit Details for every
          note type so it always appears, independent of the note's own
          subjective chief complaint. */}
      <RenderTextWithContainerPattern
        label="Reason for Visit"
        val={reportData?.chiefComplaint}
      />

      {/* ── Form-Type Specific Template ──
          Screening Assessments are rendered by each form-type template inside
          its Objective section so the report mirrors where the screeners are
          captured in the encounter UI. */}
      {getTemplateByFormType(reportData)}

      {/* ── Common Signature Footer ── */}
      <ReportContainer label="Visit Signed by">
        <div
          style={{
            pageBreakInside: "avoid",
            breakInside: "avoid",
            padding: "2px",
          }}
        >
          <Text>{`Signed by ${data?.providerName} on ${NonNullableDate(
            data?.encounterDate
          )} at ${
            data?.encounterDate ? formatTime(data?.encounterDate) : "-"
          }`}</Text>
          <img
            src={providerSignature || data?.providerSignature}
            alt="signature"
            width={"120px"}
            height={"120px"}
            style={{
              display: "block",
              objectFit: "contain",
              pageBreakInside: "avoid",
              breakInside: "avoid",
            }}
          />
        </div>
      </ReportContainer>

      <EncounterAddendumSection addendum={addendum} />
    </div>
  );
};

export default EncounterSummeryTemplate;
